import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchBar } from '@ktag/styleguide/dist/react';
import debounce from 'lodash.debounce';
import useAppState from '../../AppStore';
import { ToolNames } from '../../tools/ToolNames';
import { useFavorites } from '../../tools/hooks/useFavorites';

const SearchMobile = () => {
  const { t } = useTranslation('agisviewer');
  const searchRef = useRef<HTMLInputElement>(null);
  const hasComponentMounted = useRef(false);
  const hasUserFocusedSearchInput = useRef(false);

  const searchText = useAppState((state) => state.searchText);
  const setSearchText = useAppState((state) => state.setSearchText);
  const topicLayerFilter = useAppState((state) => state.topicLayerFilter);
  const setTopicLayerFilter = useAppState((state) => state.setTopicLayerFilter);
  const activeTool = useAppState((state) => state.activeTool);
  const setActiveTool = useAppState((state) => state.setActiveTool);
  const toggleToolbar = useAppState((state) => state.toggleToolbar);
  const isToolbarOpen = useAppState((state) => state.toolbar.isOpen);
  const setSelectedSearchResult = useAppState((state) => state.setSelectedSearchResult);

  const { getUserFavorites } = useFavorites();
  const hasFavorites = Object.entries(getUserFavorites()).length > 0;

  const isLayerSection = activeTool === ToolNames.layerSelection;
  const inputText = isLayerSection ? topicLayerFilter : searchText;

  const debounceSearchText = useCallback(
    debounce(isLayerSection ? setTopicLayerFilter : setSearchText, 300),
    [isLayerSection, setTopicLayerFilter, setSearchText],
  );
  const placeholderText = isLayerSection
    ? t('layer_selection_searchbar_placeholder')
    : t('searchbar_placeholder');

  useEffect(() => {
    hasComponentMounted.current = true;
  }, []);

  useEffect(() => {
    const handleFocusChange = () => {
      if (document.activeElement !== searchRef.current) return;

      hasUserFocusedSearchInput.current = true;

      if (isLayerSection) {
        setActiveTool(ToolNames.layerSelection, 'mapContent_tool_add_topic');
      } else if (searchText || hasFavorites) {
        setActiveTool(ToolNames.search, 'search_tool');
      }

      if (isToolbarOpen) {
        toggleToolbar();
      }
    };

    document.addEventListener('focusin', handleFocusChange);

    return () => document.removeEventListener('focusin', handleFocusChange);
  }, [activeTool, isToolbarOpen, searchText, hasFavorites]);

  useEffect(() => {
    if (!hasComponentMounted.current || !hasUserFocusedSearchInput.current) return;

    if (searchText || hasFavorites) {
      setActiveTool(ToolNames.search, 'search_tool');
      return;
    }

    setSelectedSearchResult(null);
    setActiveTool(ToolNames.identify);
  }, [searchText, hasFavorites]);

  return (
    <div className="mobile_search">
      <SearchBar
        ref={searchRef}
        placeholder={placeholderText}
        search={inputText}
        setSearch={debounceSearchText}
        small
      />
    </div>
  );
};

export default SearchMobile;
